import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  arrow as arrowMiddleware,
  autoUpdate,
  FloatingArrow,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import cx from 'classnames';

const ARROW_HEIGHT = 7;

interface TooltipProps {
  content: ReactNode;
  placement?: Placement;
  offset?: number;
  interactive?: boolean;
  arrow?: boolean;
  className?: string;
}

export const Tooltip: FC<PropsWithChildren<TooltipProps>> = ({
  content,
  placement = 'top',
  offset: offsetPx = 4,
  interactive = false,
  arrow = false,
  className,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);
  const { refs, floatingStyles, context } = useFloating({
    placement,
    middleware: [
      offset(arrow ? offsetPx + ARROW_HEIGHT : offsetPx),
      shift(),
      flip(),
      ...(arrow ? [arrowMiddleware({ element: arrowRef })] : []),
    ],
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { handleClose: interactive ? safePolygon() : null });
  const focus = useFocus(context);
  const click = useClick(context, { enabled: interactive });
  const dismiss = useDismiss(context, { referencePress: !interactive });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);

  return (
    <>
      <div ref={refs.setReference} className={className} data-open={isOpen || undefined} {...getReferenceProps()}>
        {children}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cx(
              'bg-black text-white text-xs rounded px-2 py-1 z-50',
              interactive ? 'max-w-60' : 'pointer-events-none',
            )}
            {...getFloatingProps()}
          >
            {arrow && <FloatingArrow ref={arrowRef} context={context} height={ARROW_HEIGHT} className="fill-black" />}
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};
